import { CarrierBillingAccountType } from "@redotech/cockroachdb-codegen/congodb/carrier-billing-account-type";
import { BreakdownDisplayMode } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/breakdown-display-mode";
import { StruckSnapshotSchema } from "@redotech/money";
import { CanadaPostManifestMode } from "@redotech/redo-fulfillment-carrier-account-common/canada-post";
import {
  billingAccountType,
  CarrierBillingAccount,
} from "@redotech/redo-fulfillment-carrier-account-common/carrier-billing-accounts";
import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  Carriers,
  FedexServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  APO_ENABLED_CARRIERS,
  CarrierSchema,
  isFedexCarrier,
  PO_BOX_ENABLED_CARRIERS,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { HazmatSchema } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import {
  ContentsType,
  NonDeliveryOption,
  OutboundShipmentTaxIdSchema,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import { CustomsDeclarationSchema } from "@redotech/redo-fulfillment-customs-common/customs-declaration";
import {
  FulfillmentOrderAddressSchema,
  VerifiedAddressSchema,
} from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { rateLandedCostSchema } from "@redotech/redo-fulfillment-customs-common/rate-landed-cost";
import {
  MoneySchema,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundShipmentParcelV2Schema } from "@redotech/redo-fulfillment-parcel-common/outbound-shipment-parcel-v2";
import { OutboundParcelSchema } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Some info on payment responsibilities: https://www.ups.com/assets/resources/webcontent/en_GB/UPS_Payment_Options.pdf
export enum PaymentResponsibility {
  TRANSPORTATION = "TRANSPORTATION",
  DUTIES_AND_TAXES = "DUTIES_AND_TAXES",
  VAT = "VAT",
}

export enum ShipmentPaymentOptionType {
  SENDER = "SENDER",
  THIRD_PARTY = "THIRD_PARTY",
  RECEIVER = "RECEIVER",
  COLLECT = "COLLECT",
}

export enum UspsBillingType {
  REDO = "redo",
  MERCHANT = "merchant",
}

const BasePaymentOptionSchema = z.object({
  explicit_payment_responsibilities: z
    .array(
      createEnum(PaymentResponsibility, { enumName: "PaymentResponsibility" }),
    )
    .nullish(),
});

const SenderPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.SENDER),
});

const ThirdPartyPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.THIRD_PARTY),
  carrier: createEnum(Carriers, { enumName: "Carriers" }), // Not part of the easypost schema, but needed to determine which rates to return
  account: z.string(),
  country: z.string(),
  postal_code: z.string(),
  thirdPartyBillingAccountId: z.string().nullish(),
});

export type ThirdPartyPaymentOption = z.infer<
  typeof ThirdPartyPaymentOptionSchema
>;

const ReceiverPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.RECEIVER),
  carrier: createEnum(Carriers, { enumName: "Carriers" }), // Not part of the easypost schema, but needed to determine which rates to return
  account: z.string(),
  /** UPS Bill Receiver takes no country code, but FedEx and EasyPost accept one. */
  country: z.string().nullish(),
  postal_code: z.string(),
  receiverBillingAccountId: z.string().nullish(),
});

export type ReceiverPaymentOption = z.infer<typeof ReceiverPaymentOptionSchema>;

const CollectPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z.literal(ShipmentPaymentOptionType.COLLECT),
});

export const ShipmentPaymentOptionSchema = z.discriminatedUnion("type", [
  SenderPaymentOptionSchema,
  ThirdPartyPaymentOptionSchema,
  ReceiverPaymentOptionSchema,
  CollectPaymentOptionSchema,
]);

export function isPaymentSplitDutyVat(
  paymentOption: ShipmentPaymentOption | null | undefined,
): boolean {
  if (!paymentOption) {
    return false;
  }
  return (
    paymentOption.type === ShipmentPaymentOptionType.THIRD_PARTY &&
    paymentOption.explicit_payment_responsibilities?.length === 1 &&
    paymentOption.explicit_payment_responsibilities?.[0] ===
      PaymentResponsibility.VAT
  );
}

export function isThirdPartyOnlyPayingVat(
  paymentOption: ShipmentPaymentOption | null | undefined,
): boolean {
  if (!paymentOption) {
    return false;
  }
  return !!(
    paymentOption.type === ShipmentPaymentOptionType.THIRD_PARTY &&
    paymentOption.explicit_payment_responsibilities?.includes(
      PaymentResponsibility.VAT,
    )
  );
}

/**
 * True when the carrier bills an account we don't own — a third party's or the
 * receiver's. Redo neither pays for nor sees the real price of these labels.
 */
export function isExternalAccountBilling(
  paymentOption: ShipmentPaymentOption | null | undefined,
): paymentOption is ThirdPartyPaymentOption | ReceiverPaymentOption {
  return (
    paymentOption?.type === ShipmentPaymentOptionType.THIRD_PARTY ||
    paymentOption?.type === ShipmentPaymentOptionType.RECEIVER
  );
}

export function getBillingAccountId(
  paymentOption: ShipmentPaymentOption | null | undefined,
): string | undefined {
  switch (paymentOption?.type) {
    case ShipmentPaymentOptionType.THIRD_PARTY: {
      return paymentOption.thirdPartyBillingAccountId ?? undefined;
    }
    case ShipmentPaymentOptionType.RECEIVER: {
      return paymentOption.receiverBillingAccountId ?? undefined;
    }
    default: {
      return undefined;
    }
  }
}

/** Builds the payment option a stored billing account represents. */
export function paymentOptionForBillingAccount(
  account: Pick<
    CarrierBillingAccount,
    "_id" | "carrier" | "accountNumber" | "country" | "postalCode"
  > & { billingType?: CarrierBillingAccountType | null },
  explicitPaymentResponsibilities: PaymentResponsibility[] = [],
): ThirdPartyPaymentOption | ReceiverPaymentOption {
  const shared = {
    carrier: account.carrier,
    account: account.accountNumber,
    country: account.country,
    postal_code: account.postalCode,
    explicit_payment_responsibilities: explicitPaymentResponsibilities,
  };
  if (billingAccountType(account) === CarrierBillingAccountType.Receiver) {
    return {
      ...shared,
      type: ShipmentPaymentOptionType.RECEIVER,
      receiverBillingAccountId: account._id,
    };
  }
  return {
    ...shared,
    type: ShipmentPaymentOptionType.THIRD_PARTY,
    thirdPartyBillingAccountId: account._id,
  };
}

export type ShipmentPaymentOption = z.infer<typeof ShipmentPaymentOptionSchema>;
export interface IShipmentPaymentOption extends z.ZodType<ShipmentPaymentOption> {}

export enum ShipmentRefundStatus {
  Submitted = "submitted",
  Refunded = "refunded",
  Rejected = "rejected",
  NotApplicable = "not_applicable",
  AlreadyShipped = "already_shipped", // A redo status
}

export const IB_CARRIER_ACCOUNT_ID = "IB";

export enum RateProvider {
  SOAP_BOX = "SOAP_BOX",
  EASYPOST = "EASYPOST",
  UPS = "UPS",
  LANDMARK_GLOBAL = "LANDMARK_GLOBAL",
  REDO_LANDMARK_GLOBAL = "REDO_LANDMARK_GLOBAL",
  INTERNATIONAL_BRIDGE = "INTERNATIONAL_BRIDGE",
  ETA_EXPRESS = "ETA_EXPRESS",
  TEST_CARRIER = "TEST_CARRIER",
  FEDEX = "FEDEX",
  USPS = "USPS",
  DHL_ECOMMERCE = "DHL_ECOMMERCE",
  GLOBALE = "GLOBALE",
  TIKTOK = "TIKTOK",
  CANADA_POST = "CanadaPost",
  UNIUNI = "UNIUNI",
  SENDCLOUD = "SENDCLOUD",
  STALLION_EXPRESS = "STALLION_EXPRESS",
  ECOM_EXPRESS = "ECOM_EXPRESS",
  CHITCHATS = "CHITCHATS",
}

export const rateTableProviderLabel: Record<RateProvider, string> = {
  [RateProvider.SOAP_BOX]: "Soap Box",
  [RateProvider.EASYPOST]: "EasyPost",
  [RateProvider.UPS]: "UPS",
  [RateProvider.LANDMARK_GLOBAL]: "Landmark Global",
  [RateProvider.REDO_LANDMARK_GLOBAL]: "Redo Landmark Global",
  [RateProvider.INTERNATIONAL_BRIDGE]: "International Bridge",
  [RateProvider.ETA_EXPRESS]: "ETA Express",
  [RateProvider.TEST_CARRIER]: "Test Carrier",
  [RateProvider.FEDEX]: "FedEx",
  [RateProvider.USPS]: "USPS",
  [RateProvider.DHL_ECOMMERCE]: "DHL eCommerce",
  [RateProvider.CANADA_POST]: "Canada Post",
  [RateProvider.GLOBALE]: "Global-E",
  [RateProvider.TIKTOK]: "TikTok",
  [RateProvider.UNIUNI]: "UniUni",
  [RateProvider.SENDCLOUD]: "Sendcloud",
  [RateProvider.STALLION_EXPRESS]: "Stallion Express",
  [RateProvider.ECOM_EXPRESS]: "Ecom Express",
  [RateProvider.CHITCHATS]: "Chit Chats",
};

export const ShipmentMessageSchema = z.object({
  carrier: z.string().nullish(),
  carrier_account_id: z.string().nullish(),
  message: z.string().nullish(),
  type: z.string().nullish(),
  ignored: z.boolean().nullish(),
});
export type ShipmentMessage = z.infer<typeof ShipmentMessageSchema>;

const ProviderInfoSchema_ = z.object({
  soapBox: z
    .object({
      orderId: z.string(),
      packageId: z.string(),
      externalOrderNumber: z.string().nullish(),
      batchId: z.string().nullish(),
    })
    .nullish(),
  ups: z
    .object({
      shipperId: z.string(),
      // Present when this rate is a UPS Simple Rate (flat rate); `code` is the
      // rated size (XS–XL) and is replayed when buying the label.
      simpleRate: z
        .object({
          code: z.string(),
          standardProviderCost: z.string().nullish(),
          providerCostSavings: z.string().nullish(),
        })
        .nullish(),
    })
    .nullish(),
  landmarkGlobal: z.object({ username: z.string() }).nullish(),
  easypost: z
    .object({
      childAccountId: z.string().nullish(),
      orderId: z.string().nullish(),
    })
    .nullish(),
  usps: z
    .object({
      rateIndicator: z.string().nullish(),
      processingCategory: z.string().nullish(),
      destinationEntryFacilityType: z.string().nullish(),
    })
    .nullish(),
  fedex: z
    .object({ accountNumber: z.string(), packagingType: z.string().nullish() })
    .nullish(),
  dhlEcommerce: z
    .object({
      pickupId: z.string(),
      distributionCenter: z.string(),
      redoPackageId: z.string(),
    })
    .nullish(),
  globale: z
    .object({
      globalEOrderId: z.string(),
      lineItems: z.array(z.object({ sku: z.string(), quantity: z.number() })),
    })
    .nullish(),
  tiktok: z
    .object({ shippingServiceId: z.string(), tiktokShopId: z.string() })
    .nullish(),
  uniUni: z
    .object({
      pickupWarehouseId: z.number(),
      customerId: z.string(),
      // UniUni's tracking number, returned by POST /orders/createbusinessorder.
      // Primary key for tracking lookups, POD fetches, and cancellations.
      tno: z.string().nullish(),
      // UniUni's human-readable order serial number. Secondary identifier
      // returned alongside `tno`; kept for traceability against UniUni records.
      orderSn: z.string().nullish(),
    })
    .nullish(),
  stallionExpress: z
    .object({ postageType: z.string(), shipCode: z.string().nullish() })
    .nullish(),
  ecomExpress: z
    .object({
      serviceLevelCode: z.string(),
      shipmentCode: z.string().nullish(),
      shipmentReferenceId: z.string().nullish(),
    })
    .nullish(),
  chitChats: z
    .object({
      // Chit Chats' OWN shipment id (e.g. "ABCDE12345"), assigned when we POST
      // the shipment at rating time. This is NOT the Redo `shipment_id`
      // (`redo-shp-…`) on the rate — that one loads our DB record; this one is
      // what every Chit Chats API call keys off: buy, refund, poll, and the
      // public tracking endpoint.
      chitChatsShipmentId: z.string(),
      postageType: z.string(),
      // The final-mile carrier's tracking number, populated once postage is
      // purchased. Kept for display; Redo's tracking lookups use
      // chitChatsShipmentId.
      carrierTrackingCode: z.string().nullish(),
    })
    .nullish(),
  canadaPost: z
    .object({
      manifestMode: createEnum(CanadaPostManifestMode, {
        enumName: "CanadaPostManifestMode",
      }),
      groupId: z.string().nullish(),
      manifestRequired: z.boolean().nullish(),
      shipmentId: z.string().nullish(),
      trackingPin: z.string().nullish(),
      supportsReturnSpec: z.boolean().nullish(),
      returnLabelService: ServiceSchema.nullish(),
      nativeReturnLabel: z.boolean().nullish(),
    })
    .nullish(),
});
export const ProviderInfoSchema: z.ZodType<ProviderInfo> = ProviderInfoSchema_;
export interface ProviderInfo extends z.infer<typeof ProviderInfoSchema_> {}

// This is information necessary for Redo rate logic that is not provided by the shipping provider
// on a rate refresh. This information will not change if the shipment has not changed.
export const SupplementalRefreshInfoSchema = z.object({
  fedex_zone: z.number(),
  usps_zone: z.number(),
  saturday_delivery: z.boolean(),
  is_merchant_carrier_account: z.boolean(),
  is_parent_easypost_account: z.boolean().nullish(),
  providerInfo: ProviderInfoSchema.nullish(),
});
export type SupplementalRefreshInfo = z.infer<
  typeof SupplementalRefreshInfoSchema
>;

// One parcel's share of a multi-parcel V2 rate. `shipmentId` is the persisted
// child outbound-shipment doc created at rating time; `provider_cost`/`rate`
// are USD and per-parcel (spread already applied to `rate`).
const parcelRateSchema = z.object({
  shipmentId: z.string(),
  parcel: OutboundShipmentParcelV2Schema,
  provider_cost: z.string(),
  rate: z.string(),
  currency: z.string(),
  providerInfo: ProviderInfoSchema.nullish(),
});
export type ParcelRate = z.infer<typeof parcelRateSchema>;

export const rawOutboundRateSchema = z.object({
  id: z.string(),
  shipment_id: z.string(),
  currency: z.string(),
  carrier: CarrierSchema,
  service: ServiceSchema,
  delivery_date: z.string().nullish(),
  delivery_days: z.number().nullish(),
  /**
   * Number of carrier-valid delivery days the package will spend in transit
   * (i.e. the carrier's SLA expressed as a count). Stable across quote days,
   * unlike `delivery_days`, which some providers compute from "now" so it
   * drifts when quoted on weekends. Use this for cross-provider comparison
   * and automation-driven selection. Falls back to `delivery_days` when not
   * populated.
   */
  transit_days: z.number().nullish(),
  delivery_date_guaranteed: z.boolean().nullish(),
  created_at: z.string(),
  updated_at: z.string(),
  carrier_account_id: z.string(),
  billing_type: z.string(),
  provider_cost: z.string(),
  rev_share_margin: z.string().nullish(), // This does not factor into the rate we or the merchant pays, but is used for competitive spread and reporting
  fedex_zone: z.number(),
  usps_zone: z.number(),
  ups_zone: z.number().nullish(),
  uniuni_zone: z.number().nullish(),
  mode: z.string(),
  refund_status: createEnum(ShipmentRefundStatus, {
    enumName: "ShipmentRefundStatus",
  }).nullish(),
  saturday_delivery: z.boolean(),
  provider: createEnum(RateProvider, { enumName: "RateProvider" }).default(
    RateProvider.EASYPOST,
  ),
  is_merchant_carrier_account: z.boolean(),
  is_parent_easypost_account: z.boolean().nullish(),
  payment_option: ShipmentPaymentOptionSchema.nullish(),
  providerInfo: ProviderInfoSchema.nullish(),
  /**
   * See the V2 outbound shipping provider for an explanation for the purpose of this field.
   */
  version: z.number().nullish(),
  /**
   * Per-parcel breakdown for a multi-parcel V2 rate: one entry per parcel,
   * carrying its persisted child shipment id, the parcel, and the parcel's own
   * provider cost and (per-parcel-spread-applied) rate, all converted to USD.
   * The top-level provider_cost/rate are the whole-shipment authoritative
   * numbers; these are the parts beneath them. Populated only when the shipment
   * has more than one parcel — single-parcel and V1 rates leave it null. Parcel
   * count is read via outboundRateParcelCount.
   */
  ratesByParcel: z.array(parcelRateSchema).nullish(),
});

export type RawOutboundRate = z.infer<typeof rawOutboundRateSchema>;

const RateSchema_ = rawOutboundRateSchema.extend({
  rate: z.string(),
  competitive_spread_applied: z.boolean().nullish(),
  redo_competitive_spread_applied: z.boolean().nullish(),
  rev_share_margin: z.string().nullish(),
  automation_priority_rate: z.boolean().nullish(),
  provider_cost: z.string().nullish(), // Nullish for backwards compatibility
  fedex_zone: z.number().nullish(), // Nullish for backwards compatibility
  usps_zone: z.number().nullish(), // Nullish for backwards compatibility
  ups_zone: z.number().nullish(), // Nullish for backwards compatibility
  uniuni_zone: z.number().nullish(), // Nullish for backwards compatibility
  mode: z.string().nullish(), // Nullish for backwards compatibility
  is_merchant_carrier_account: z.boolean().nullish(), // Nullish for backwards compatibility
  is_parent_easypost_account: z.boolean().nullish(), // Nullish for backwards compatibility
  billing_type: z.string().nullish(), // Nullish for backwards compatibility
  saturday_delivery: z.boolean().nullish(), // Nullish for backwards compatibility
  landedCost: rateLandedCostSchema.nullish(),
  // Bought rate frozen in store currency at purchase, for frozen display.
  struck: StruckSnapshotSchema.nullish(),
  // Safe client projection populated before providerInfo is stripped.
  simple_rate_code: z.string().nullish(),
});

export const RateSchema = RateSchema_.extend({
  multiParcelRates: z.array(RateSchema_).nullish(),
});

export type OutboundRate = z.infer<typeof RateSchema>;

/**
 * Number of labels a rate will produce. V1 multi-parcel rates carry one entry
 * per parcel in multiParcelRates; V2 multi-parcel rates carry one entry per
 * parcel in ratesByParcel. Falls back to 1 for single-parcel rates, which carry
 * neither field.
 */
export function outboundRateParcelCount(rate: OutboundRate): number {
  return rate.multiParcelRates?.length ?? rate.ratesByParcel?.length ?? 1;
}

export const CheckoutOutboundRateSchema = RateSchema.pick({
  provider_cost: true,
  rate: true,
  carrier: true,
  service: true,
  delivery_days: true,
  currency: true,
  shipment_id: true,
  carrier_account_id: true,
}).extend({
  shipment_id: z.string().optional(),
  carrier_account_id: z.string().optional(),
  landedCostTotal: z.number().optional(),
  landedCostBreakdown: z
    .object({ duties: z.number(), taxes: z.number(), fees: z.number() })
    .optional(),
  landedCostBreakdownOriginal: z
    .object({ duties: z.number(), taxes: z.number(), fees: z.number() })
    .optional(),
  breakdownDisplayMode: createEnum(BreakdownDisplayMode, {
    enumName: "BreakdownDisplayMode",
  }).optional(),
  breakdownLabelOverrides: z
    .object({
      shipping: z.string().optional(),
      taxes: z.string().optional(),
      dutiesAndFees: z.string().optional(),
    })
    .optional(),
});

export type CheckoutOutboundRate = z.infer<typeof CheckoutOutboundRateSchema>;

export const OutboundLabelShippingAddressSchema = z.object({
  name: z.string().nullish(),
  address1: z.string(),
  address2: z.string().nullish(),
  city: z.string(),
  country: z.string().nullish(),
  country_code: z.string().nullish(),
  province: z.string().nullish(),
  province_code: z.string().nullish(),
  zip: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  company: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string(),
});

/**
 * Canada Post shipment options. Delivery instructions (DNS, HFP, LAD) and proof
 * of age (PA18, PA19, PA21) have complex cross-conflicts per the Canada Post API,
 * so we model every valid combination as a single enum to make invalid states
 * unrepresentable. The only valid cross-group combo is PA18 + HFP.
 */
export enum CanadaPostOption {
  DO_NOT_SAFE_DROP = "DNS",
  CARD_FOR_PICKUP = "HFP",
  LEAVE_AT_DOOR = "LAD",
  PROOF_OF_AGE_18 = "PA18",
  PROOF_OF_AGE_19 = "PA19",
  PROOF_OF_AGE_21 = "PA21",
  PROOF_OF_AGE_18_CARD_FOR_PICKUP = "PA18+HFP",
}

export const CANADA_POST_OPTION_LABELS: Record<CanadaPostOption, string> = {
  [CanadaPostOption.DO_NOT_SAFE_DROP]: "Do not safe drop",
  [CanadaPostOption.CARD_FOR_PICKUP]: "Card for pickup",
  [CanadaPostOption.LEAVE_AT_DOOR]: "Leave at door",
  [CanadaPostOption.PROOF_OF_AGE_18]: "Proof of age (18+)",
  [CanadaPostOption.PROOF_OF_AGE_19]: "Proof of age (19+)",
  [CanadaPostOption.PROOF_OF_AGE_21]: "Proof of age (21+)",
  [CanadaPostOption.PROOF_OF_AGE_18_CARD_FOR_PICKUP]:
    "Proof of age (18+) with card for pickup",
};

export enum SpecialRateEligibility {
  MEDIA_MAIL = "USPS.MEDIAMAIL",
  LIBRARY_MAIL = "USPS.LIBRARYMAIL",
  MEDIA_MAIL_AND_LIBRARY_MAIL = "USPS.MEDIAMAIL,USPS.LIBRARYMAIL",
  DHL_BPM = "DHL.BPM",
}

export function includesMediaMail(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return (
    eligibility === SpecialRateEligibility.MEDIA_MAIL ||
    eligibility === SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
  );
}

export function includesLibraryMail(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return (
    eligibility === SpecialRateEligibility.LIBRARY_MAIL ||
    eligibility === SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
  );
}

export function includesDhlBpm(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return eligibility === SpecialRateEligibility.DHL_BPM;
}

const ShipmentRatesSchema_ = z.object({
  easypostShipmentId: z.string().nullish(),
  easypostOrderId: z.string().nullish(),
  // This is the parcel assigned to a purchased shipment rate
  parcel: OutboundParcelSchema.nullish(),
  // Since we treat multi-parcel shipment rates as a single "Rate", we use this to calculate ratesOutdated
  parcelsUsedToCalculateRate: z.array(OutboundParcelSchema).nullish(),
  mode: z.string(),
  rates: z.array(RateSchema),
  /**  */
  weight: WeightSchema,
  originAddress: z
    .union([FulfillmentOrderAddressSchema, VerifiedAddressSchema])
    .nullish(),
  destinationAddress: z
    .union([FulfillmentOrderAddressSchema, VerifiedAddressSchema])
    .nullish(),
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility, {
    enumName: "SpecialRateEligibility",
  }).nullish(),
  directDeliveryOnly: z.boolean().nullish(),
  carbonNeutral: z.boolean().nullish(),
  canadaPostOption: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  }).nullish(),
  nonDeliveryOption: createEnum(NonDeliveryOption, {
    enumName: "NonDeliveryOption",
  }).nullish(),
  ddp: z.boolean().nullish(),
  returnAddress: FulfillmentOrderAddressSchema.nullish(),
  shipmentIds: z.array(z.string()).nullish(),
  shipmentMessages: z
    .record(z.string(), z.array(ShipmentMessageSchema))
    .nullish(),
  createdAt: z.date().nullish(),
  itn: z.string().nullish(),
  /** @deprecated */
  dryIceWeight: WeightSchema.nullish(),
  /** @deprecated */
  hazmat: HazmatSchema.nullish(),
  taxIdentifierIds: z.array(z.string()).nullish(),
  customerTaxIds: z.array(OutboundShipmentTaxIdSchema).nullish(),
  uspsExemptionCode: createEnum(USPSExemptionCode, {
    enumName: "USPSExemptionCode",
  }).nullish(),
  additionalCustomsComments: z.string().nullish(),
  contentsExplanation: z.string().nullish(),
  contentsType: createEnum(ContentsType, {
    enumName: "ContentsType",
  }).nullish(),
  customsDeclarations: z.array(CustomsDeclarationSchema).nullish(),
  refreshRatesAt: z.date().nullish(),
});

export const ShipmentRatesSchema: z.ZodType<ShipmentRates> =
  ShipmentRatesSchema_;

export interface ShipmentRates extends z.infer<typeof ShipmentRatesSchema_> {}

// If the rate came from a child account (aka it's a merchant rate) but easypost still tells us that it's billed through easypost, we need to charge for the label
/** Billing-relevant subset of a rate, so callers holding only these fields
 * (e.g. passed over the wire) can decide chargeability without a full rate. */
export type ShouldChargeForLabelFields = Pick<
  OutboundRate,
  | "provider"
  | "is_parent_easypost_account"
  | "payment_option"
  | "is_merchant_carrier_account"
  | "billing_type"
>;

/** The fields the provider factory actually reads to route to a provider, so
 * a rate rebuilt from a doc snapshot (void) needs no filler values. */
export type RoutableOutboundRate = Pick<
  OutboundRate,
  | "version"
  | "provider"
  | "carrier"
  | "carrier_account_id"
  | "billing_type"
  | "is_merchant_carrier_account"
  | "is_parent_easypost_account"
  | "providerInfo"
>;

export function shouldChargeForLabel(rate: ShouldChargeForLabelFields) {
  // Even though we treat all providers the same right now, we want to force developers to consider about this logic when they add a new provider
  // Addint this check ensures we think about this logic when we add a new provider
  switch (rate.provider) {
    case RateProvider.EASYPOST:
    case RateProvider.UPS:
    case RateProvider.LANDMARK_GLOBAL:
    case RateProvider.REDO_LANDMARK_GLOBAL:
    case RateProvider.INTERNATIONAL_BRIDGE:
    case RateProvider.ETA_EXPRESS:
    case RateProvider.SOAP_BOX:
    case RateProvider.FEDEX:
    case RateProvider.TEST_CARRIER:
    case RateProvider.DHL_ECOMMERCE:
    case RateProvider.UNIUNI:
    case RateProvider.CANADA_POST:
    case RateProvider.GLOBALE:
      // If a merchant has brought their own parent easypost account, all of the billing is handled outside of redo
      if (rate.is_parent_easypost_account) {
        return false;
      }

      if (isExternalAccountBilling(rate.payment_option)) {
        return false;
      }

      return (
        !rate.is_merchant_carrier_account || rate.billing_type === "easypost"
      );
    case RateProvider.USPS:
      // USPS doesn't support external account billing, but adding this just in case for future
      if (isExternalAccountBilling(rate.payment_option)) {
        return false;
      }

      return rate.billing_type === UspsBillingType.REDO;
    case RateProvider.TIKTOK:
      return false; // We never pay for these labels, so we should never charge for them
    case RateProvider.SENDCLOUD:
      // Sendcloud is returns-only and only routes through merchant accounts;
      // outbound code paths should never reach this branch for Sendcloud rates.
      return false;
    case RateProvider.STALLION_EXPRESS:
      // Stallion Express is BYOA only — merchant pays the carrier directly.
      return false;
    case RateProvider.ECOM_EXPRESS:
      // Ecom Express is BYOA only — merchant pays the carrier directly.
      return false;
    case RateProvider.CHITCHATS:
      // Chit Chats is BYOA only — merchant pays the carrier directly.
      return false;
    default:
      assertNever(rate.provider);
  }
}

export function isMultiParcelShipmentRates(
  shipmentRates: ShipmentRates,
): boolean {
  return (
    !!shipmentRates.parcelsUsedToCalculateRate &&
    shipmentRates.parcelsUsedToCalculateRate.length > 1
  );
}

export function getShipmentParcels(shipmentRates: ShipmentRates) {
  if (isMultiParcelShipmentRates(shipmentRates)) {
    return shipmentRates.parcelsUsedToCalculateRate!;
  }
  return shipmentRates.parcel ? [shipmentRates.parcel] : [];
}

export function isPoBoxEnabledRate(
  rate: OutboundRate,
  country?: string | null,
): boolean {
  // FedEx Ground Economy (Smart Post) is a hybrid service that uses USPS for final-mile delivery,
  // so it can deliver to PO Boxes
  if (
    isFedexCarrier(rate.carrier) &&
    rate.service === FedexServices.SMART_POST
  ) {
    return true;
  }
  if (PO_BOX_ENABLED_CARRIERS.includes(rate.carrier)) {
    return true;
  }
  const normalizedCountry = country?.toUpperCase();
  if (normalizedCountry === "CA" && rate.carrier === Carriers.CANADA_POST) {
    return true;
  }
  if (normalizedCountry === "AU" && rate.carrier === Carriers.AUSTRALIA_POST) {
    return true;
  }
  return false;
}

export function isAPOEnabledRate(rate: OutboundRate) {
  // Out UPS integration is not ready to handle APO addresses, they need us to make changes to the integration
  return (
    APO_ENABLED_CARRIERS.includes(rate.carrier) &&
    rate.provider !== RateProvider.UPS
  );
}

export const RETRYABLE_ERROR_MESSAGES = [
  /** Easypost returns this rates message for any carrier that does not provide rates within 10 secs. We have seen this mostly with FirstMile, but also occasionally DHL, APC */
  "Carrier did not respond within 10 seconds.",
  /** Received on Amazon rates due to being rate limited by Amazon (not an EasyPost rate limit) */
  "You exceeded your quota for the requested resource.",
  /** EasyPost sometimes disables FM for a few minutes because they detect performance issues */
  "Rating with FirstMileConciseAccount (production mode) is temporarily disabled due to carrier performance issues",
  /** Returned when direct UPS rates are unavailable due to 503 responses */
  "UPS rates are temporarily unavailable",
  /** Returned by Easypost when they can't contact the carrier. Seen with UPS so far */
  "The carrier failed to respond, please try again.",
  /** Returned by Amazon when there are insufficient funds in our account */
  "Unable to print due to insufficient credit balance.",
  /** Transient error when retrieving Amazon rates */
  "Unable to retrieve AmazonShipping rates. Please try again or contact support",
  /** Returned by Easypost when they can't contact the carrier. Seen with Ontrac so far */
  "Rating with OntracV3Account (production mode) is temporarily disabled due to carrier performance issues",
  /** Transient error returned by several direct integrations */
  "Connection aborted",
  /** Transient carrier response that could not be parsed — safe to retry */
  "The carrier returned a response we were not able to process. Please try again later.",
  /** EasyPost returns this when they detect performance issues with the carrier */
  "is temporarily disabled due to carrier performance issues",
  /** Transient request timeout — seen with DHL eCommerce when the carrier is slow to respond */
  "timeout of 8000ms exceeded",
] as const;

export const outboundLabelErrorSchema = z.object({
  message: z.string(),
  retryable: z.boolean(),
});

export type OutboundLabelError = z.infer<typeof outboundLabelErrorSchema>;

export const carrierShipmentSchema = z.object({
  carrier: CarrierSchema,
  service: ServiceSchema,
  redoShipmentId: z.string(),
  rate: MoneySchema,
  providerCost: MoneySchema,
  commercialInvoiceUrl: z.string().nullish(),
  comInvoiceSubmittedElectronically: z.boolean().nullish(),
  shipmentPackages: z.array(
    z.object({
      trackingNumber: z.string(),
      labelUrl: z.string(),
      trackingUrl: z.string().nullish(),
      rate: MoneySchema,
      providerCost: MoneySchema,
    }),
  ),
});

export type CarrierShipment = z.infer<typeof carrierShipmentSchema>;
